import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { TextAreaContainer } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
  ModalSheetHeader,
  LeftSidePanel,
  LeftSidePanelHeading,
  LeftSidePanelTitle,
  LeftSidePanelDescription,
} from 'components/ModalSheet'
import { match } from 'utils/pattern-match-util'
import styles from './RequestScopeChangesDialog.sass'
import RequestScopeChangesViewModel, {
  RequestScopeChangesState,
} from './RequestScopeChangesViewModel'
import SearchField from 'components/SearchField'
import { useActivation } from 'hooks/mobx-hooks'
import ScopeChangeInventoryItem from './ListItems/ScopeChangeInventoryItem'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import ScopeChangeCustomItem from './ListItems/ScopeChangeCustomItem'

interface RequestScopeChangesDialogProps {
  vm: RequestScopeChangesViewModel
}

/**
 * The RequestScopeChangesDialog
 */
export default observer(function RequestScopeChangesDialog({
  vm,
}: RequestScopeChangesDialogProps) {
  const disabled = vm.request.pending
  useActivation(vm)

  const modalContentRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0
    }
  }, [vm.state])

  const isCurrentStateSuccessfulOrFailed =
    vm.state === RequestScopeChangesState.Success ||
    vm.state === RequestScopeChangesState.Failed

  const canShowAddCustomItemEmptyState =
    vm.isServiceItemsEmpty &&
    vm.scopeChangeCustomItems.length === 0 &&
    vm.searchText.value !== '' &&
    vm.inventoryScrollProvider.isDone &&
    vm.canCreateCustomJobScopeItemRequest

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick={disabled}
      disableCloseOnEsc={disabled}
      leftSidePanelContent={
        isCurrentStateSuccessfulOrFailed ? undefined : (
          <RequestScopeChangesDialogLeftSidePanelContent
            requestScopeChangesState={vm.state}
          />
        )
      }
    >
      {match(vm.state, {
        [RequestScopeChangesState.RequestingChanges]: () => (
          <>
            <RequestScopeChangeHeading title="Select all items to update">
              <div className={styles.searchContainer}>
                <SearchField
                  autoFocus
                  stretch
                  className={styles.scopeSearch}
                  placeholder="Type to search"
                  onChange={vm.searchText.change}
                  value={vm.searchText.value}
                  dataTest="request-scope-changes-search-field"
                  showClearButton
                />
              </div>
            </RequestScopeChangeHeading>

            <GreyContainer centerContent>
              <ModalSheetContent ref={modalContentRef}>
                {canShowAddCustomItemEmptyState ? (
                  <div className={styles.addCustomItemContainerEmpty}>
                    <div className={styles.customItemTextEmpty}>
                      There are no service items with that name.
                    </div>
                    <Button
                      type="text"
                      onClick={vm.addScopeChange}
                      dataTest="request-scope-changes-add-button"
                    >
                      Add custom service
                    </Button>
                  </div>
                ) : (
                  <div className={styles.itemsContainer}>
                    {vm.serviceItems.map((item) => {
                      return (
                        <ScopeChangeInventoryItem
                          key={item.versionId}
                          item={item}
                        />
                      )
                    })}
                    {vm.scopeChangeCustomItems.map((item, index) => {
                      return (
                        <ScopeChangeCustomItem
                          key={item.key}
                          item={item}
                          remove={vm.removeScopeChange}
                          dataTestId={index}
                        />
                      )
                    })}
                    <InfiniteScrollLoader
                      compact
                      scrollProvider={vm.inventoryScrollProvider}
                    />
                    {vm.canCreateCustomJobScopeItemRequest && (
                      <div className={styles.addCustomItemContainer}>
                        <div className={styles.customItemText}>
                          Not what you’re looking for?
                        </div>
                        <Button
                          type="text"
                          onClick={vm.addScopeChange}
                          dataTest="request-scope-changes-add-button"
                        >
                          Add custom service
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </ModalSheetContent>
            </GreyContainer>

            <ModalSheetFooter>
              <FooterInventoryItemsQuantityInfo
                totalSelectedInventoryItemsCount={
                  vm.totalSelectedInventoryItemsCount
                }
              />
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  type="primary"
                  onClick={vm.moveToQuantityStep}
                  disabled={!vm.itemsSelected}
                  dataTest="request-scope-changes-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.UpdatingQuantity]: () => (
          <>
            <RequestScopeChangeHeading title="Compare and adjust quantities">
              <ItemHeadings vm={vm} />
            </RequestScopeChangeHeading>

            <GreyContainer>
              <ModalSheetContent ref={modalContentRef}>
                <div className={styles.itemsContainer}>
                  {vm.selectedInventoryItems.map((item) => {
                    return (
                      <ScopeChangeInventoryItem
                        updateQuantity
                        key={item.versionId}
                        item={item}
                      />
                    )
                  })}
                  {vm.scopeChangeCustomItems.map((item, index) => {
                    return (
                      <ScopeChangeCustomItem
                        updateQuantity
                        key={item.key}
                        item={item}
                        remove={vm.removeScopeChange}
                        dataTestId={index}
                      />
                    )
                  })}
                </div>
              </ModalSheetContent>
            </GreyContainer>

            <ModalSheetFooter>
              <FooterInventoryItemsQuantityInfo
                totalSelectedInventoryItemsCount={
                  vm.totalSelectedInventoryItemsCount
                }
              />
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    vm.setState(RequestScopeChangesState.RequestingChanges)
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  onClick={vm.moveToSubmissionStep}
                  disabled={!vm.quantitiesChanged}
                  dataTest="request-scope-changes-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.ReviewingChanges]: () => (
          <>
            <RequestScopeChangeHeading title="Review and submit changes">
              <ItemHeadings vm={vm} />
            </RequestScopeChangeHeading>

            <div className={styles.flexContainer}>
              <ModalSheetContent ref={modalContentRef} expandable>
                {vm.relevantSelectedInventoryItems.map((item) => (
                  <div className={styles.listItem} key={item.versionId}>
                    <div className={styles.itemContainer}>
                      <span>
                        <strong>{item.name}</strong>
                      </span>
                      <div className={styles.itemDescription}>
                        {item.description}
                      </div>
                    </div>

                    <span>
                      <strong>{item.newQuantity}</strong>
                    </span>
                  </div>
                ))}
                {vm.scopeChangeCustomItems.map((item) => (
                  <div className={styles.listItem} key={item.key}>
                    <span>
                      <strong>{item.name}</strong>
                    </span>
                    <span>
                      <strong>{item.quantity}</strong>
                    </span>
                  </div>
                ))}
                <div className={styles.listItemSeparator}></div>
                <div className={styles.filler}></div>
                <ModalSheetContentBlock expanded>
                  <TextAreaContainer
                    label="Reason"
                    model={vm}
                    field="reason"
                    name="reason"
                    data-test="request-scope-changes-reason-input"
                    help={`${vm.reasonCharsRemaining} characters remaining.`}
                    error={vm.submissionValidationStep.getError('reason')}
                    focusOnError
                  />
                </ModalSheetContentBlock>
              </ModalSheetContent>
            </div>

            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    vm.setState(RequestScopeChangesState.UpdatingQuantity)
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  loading={vm.request.pending}
                  onClick={vm.request}
                  dataTest="request-scope-changes-submit-button"
                >
                  Send Updates
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Success]: () => (
          <>
            <ModalSheetHeader withCloseButton />
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentIllustration
                  src={require('assets/images/img_welcome.svg')}
                />
                <ModalSheetContentTitle dataTest="request-scope-changes-success">
                  Thank you for your request
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  A Customer Success Representative will review and notify you
                  when updates have been approved.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={vm.close}>Close</Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Failed]: () => (
          <>
            <ModalSheetHeader withCloseButton />
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Unable to request scope changes
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {vm.error ? (
                    vm.error
                  ) : (
                    <p>
                      We were unable to request scope changes, it's possible
                      that the request currently is not in a state where changes
                      are available, or it could be a transient error. Please
                      try again. If the issue persists, please contact an
                      administrator.
                    </p>
                  )}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button disabled={disabled} onClick={vm.close}>
                  Cancel
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
})

const FooterInventoryItemsQuantityInfo = observer(
  function FooterInventoryItemsQuantityInfo({
    totalSelectedInventoryItemsCount,
  }: {
    totalSelectedInventoryItemsCount: number
  }) {
    return (
      <div className={styles.footerInfo}>
        {totalSelectedInventoryItemsCount} selected
      </div>
    )
  },
)

const RequestScopeChangesDialogLeftSidePanelContent = observer(
  function RequestScopeChangesDialogLeftSidePanelContent({
    requestScopeChangesState,
  }: {
    requestScopeChangesState: RequestScopeChangesState
  }) {
    return (
      <LeftSidePanel>
        {match(requestScopeChangesState, {
          [RequestScopeChangesState.RequestingChanges]: () => (
            <>
              <LeftSidePanelHeading>1 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Select items to update</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Select all items that need to be either updated or removed
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.UpdatingQuantity]: () => (
            <>
              <LeftSidePanelHeading>2 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Update quantities</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Compare current item totals and capture new quantities
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.ReviewingChanges]: () => (
            <>
              <LeftSidePanelHeading>3 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Review changes</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Confirm updates and share why changes are being requested
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.Success]: () => undefined,
          [RequestScopeChangesState.Failed]: () => undefined,
        })}
      </LeftSidePanel>
    )
  },
)

const RequestScopeChangeHeading = observer(function RequestScopeChangeHeading({
  title,
  children,
}: {
  title: string
  children?: React.ReactNode
}) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.closeButtonContainer}>
            <ModalSheetHeaderCloseButton />
          </div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
})

const ItemHeadings = observer(function ItemHeadings({
  vm,
}: {
  vm: RequestScopeChangesViewModel
}) {
  return (
    <div className={styles.listItem}>
      <span>Service item</span>

      {vm.state === RequestScopeChangesState.UpdatingQuantity && (
        <span className={styles.listItemCurrent}>
          Current &nbsp;
          <Tooltip
            content={'Current quantity for each item on the job.'}
            placement="top"
          >
            <Icon name="info" size={18} color="var(--blue400)" />
          </Tooltip>
        </span>
      )}
      <span
        className={
          vm.state === RequestScopeChangesState.UpdatingQuantity
            ? styles.listItemNewTotalQuantitesState
            : styles.listItemNewTotal
        }
      >
        New total &nbsp;
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
    </div>
  )
})

const GreyContainer = observer(function GreyContainer({
  children,
  centerContent = false,
}: {
  children: React.ReactNode
  centerContent?: boolean
}) {
  return (
    <div
      className={cx(
        styles.greyBackround,
        centerContent && styles.centerContent,
      )}
    >
      {children}
    </div>
  )
})
